import { sessionExpiredService } from '../services/sessionExpiredService';
import { AuthenticationError } from './authErrors';
import {
  CURRENT_SHOP_HEADER,
  clearStoredCurrentShopId,
  readCurrentShopIdFromToken,
  readUserIdFromToken,
  withShopScopeQuery,
} from './currentShop';

export { AuthenticationError, isAuthenticationError, shouldSuppressErrorForAuth } from './authErrors';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export const getApiUrl = (endpoint: string): string => {
  if (API_BASE_URL) {
    return `${API_BASE_URL}${endpoint}`;
  }
  return endpoint;
};

/**
 * Determine the correct refresh-token endpoint based on user type stored in the JWT.
 * Falls back to admin endpoint if the user type cannot be determined.
 */
const getRefreshEndpoint = (): string => {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.userType === 'PRACTITIONER') return getApiUrl('/api/practitioner-auth/refresh-token');
      if (payload.userType === 'EMPLOYEE') return getApiUrl('/api/employee-auth/refresh-token');
    }
  } catch {
    // ignore parse errors
  }
  return getApiUrl('/api/auth/refresh-token');
};

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

/**
 * Attempt to silently renew the access token using the stored refresh token.
 * Returns the new access token on success, null on failure.
 */
const silentRefresh = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;

  try {
    const response = await fetch(getRefreshEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-store' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (!data.success || !data.token) return null;

    // Persist the new tokens
    localStorage.setItem('token', data.token);
    if (data.refreshToken) {
      localStorage.setItem('refresh_token', data.refreshToken);
    }

    return data.token;
  } catch {
    return null;
  }
};

/**
 * Centralized API client with automatic token refresh on 401.
 * If the silent refresh succeeds the original request is retried transparently.
 * Only if refresh fails does it show the session-expired modal.
 */
export const apiClient = async (
  endpoint: string,
  options: RequestInit = {},
  requireAuth: boolean = true
): Promise<Response> => {
  const url = withShopScopeQuery(getApiUrl(endpoint), options.method ?? 'GET');
  const isFormData = options.body instanceof FormData;

  const buildHeaders = (token?: string | null): Record<string, string> => {
    const h: Record<string, string> = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(options.headers as Record<string, string>),
      'Cache-Control': 'no-store',
    };
    const t = token ?? (requireAuth ? localStorage.getItem('token') : null);
    if (t) h['Authorization'] = `Bearer ${t}`;
    const shopId = readCurrentShopIdFromToken();
    if (shopId) h[CURRENT_SHOP_HEADER] = shopId;
    return h;
  };

  // First attempt
  const response = await fetch(url, {
    ...options,
    headers: buildHeaders(),
    cache: options.cache ?? 'no-store',
  });

  // Stale/invalid cached shop selection → clear it and retry once with the account's default shop
  if (response.status === 403) {
    const cloned = response.clone();
    try {
      const body = await cloned.json();
      if (body?.code === 'SHOP_ACCESS_DENIED') {
        const userId = readUserIdFromToken();
        if (userId) clearStoredCurrentShopId(userId);
        return fetch(url, {
          ...options,
          headers: buildHeaders(),
          cache: options.cache ?? 'no-store',
        });
      }
    } catch {
      // response body wasn't JSON — fall through and return the original 403
    }
    return response;
  }

  if (response.status !== 401) {
    return response;
  }

  // ── 401: try silent refresh ──────────────────────────────────────────────
  if (sessionExpiredService.getIsShowing()) {
    throw new AuthenticationError();
  }

  if (isRefreshing) {
    // Another call is already refreshing — wait for it then retry
    return new Promise<Response>((resolve, reject) => {
      subscribeTokenRefresh(async (newToken) => {
        try {
          const retried = await fetch(url, {
            ...options,
            headers: buildHeaders(newToken),
            cache: options.cache ?? 'no-store',
          });
          resolve(retried);
        } catch (e) {
          reject(e);
        }
      });
    });
  }

  isRefreshing = true;
  const newToken = await silentRefresh();
  isRefreshing = false;

  if (newToken) {
    onTokenRefreshed(newToken);
    // Retry the original request with the fresh token
    return fetch(url, {
      ...options,
      headers: buildHeaders(newToken),
      cache: options.cache ?? 'no-store',
    });
  }

  // Refresh failed → show session expired modal
  sessionExpiredService.showSessionExpired('Votre session a expiré. Veuillez vous reconnecter.');
  throw new AuthenticationError();
};
